// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsDashboardPage
 * @package @geenius/analytics/react
 * @description Composes the primary React analytics dashboard page from the
 * shared metric, table, and device widgets. The page exposes a ready-to-render
 * overview surface for traffic and engagement reporting.
 */

import {
  type AnalyticsDashboardData,
  type AnalyticsEvent,
  type CohortAnalysis,
  type FunnelResult,
  type RealtimeStats,
  buildAnalyticsSeoMeta,
} from "@geenius/analytics";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@geenius/ui/react";
import type React from "react";
import { DeviceBreakdown } from "../components/DeviceBreakdown";
import { EventTable } from "../components/EventTable";
import { FunnelChart } from "../components/FunnelChart";
import { RealtimeBadge } from "../components/RealtimeBadge";
import { StatsCard } from "../components/StatsCard";
import { TopPagesTable } from "../components/TopPagesTable";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import {
  ANALYTICS_MOTION_DELAYS,
  FadeIn,
  getFadeInTransition,
} from "../lib/motion";

export type AnalyticsSeoMeta = ReturnType<typeof buildAnalyticsSeoMeta>;

type DashboardIconName =
  | "views"
  | "visitors"
  | "referrers"
  | "devices"
  | "conversion";

const DASHBOARD_ICON_PATHS: Record<DashboardIconName, string> = {
  views:
    "M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Zm9.5 3.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z",
  visitors:
    "M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0Zm-10 9a6 6 0 0 1 12 0v1H6v-1Zm12.5-8a3 3 0 0 0-1.4-5.6M20 21v-.5a4.5 4.5 0 0 0-3-4.2",
  referrers:
    "M10 14a4 4 0 0 0 5.7.2l2.1-2.1a4 4 0 0 0-5.7-5.7l-1.2 1.2M14 10a4 4 0 0 0-5.7-.2l-2.1 2.1a4 4 0 0 0 5.7 5.7l1.2-1.2",
  devices: "M4 5h16v10H4V5Zm3 14h10M9 15v4m6-4v4",
  conversion: "M4 19V5m0 14h16M8 15l3-3 3 2 5-7",
};

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

function DashboardIcon({
  name,
}: {
  name: DashboardIconName;
}): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      className="h-5 w-5 text-primary"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    >
      <path d={DASHBOARD_ICON_PATHS[name]} />
    </svg>
  );
}

/**
 * Props for rendering the React analytics dashboard page.
 *
 * @property data Optional aggregate dashboard data rendered across the page sections.
 * @property recentEvents Optional list of recent analytics events shown in the event table.
 * @property funnelResult Optional funnel analysis rendered as a conversion section.
 * @property cohortAnalysis Optional cohort analysis rendered as a retention section.
 * @property realtimeStats Optional realtime analytics snapshot rendered in the page header and detail section.
 * @property conversionRate Optional conversion percentage rendered as a KPI card.
 * @property isLoading Whether child widgets should render loading placeholders instead of content.
 * @property isRealtimeLoading Whether the realtime section is still loading.
 * @property error Optional page-level error message or object.
 * @property onRetry Optional retry handler rendered when an error is present.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property className Optional class name merged into the page wrapper.
 */
export interface AnalyticsDashboardPageProps {
  data?: AnalyticsDashboardData | null;
  recentEvents?: AnalyticsEvent[];
  funnelResult?: FunnelResult | null;
  cohortAnalysis?: CohortAnalysis | null;
  realtimeStats?: RealtimeStats | null;
  conversionRate?: number | null;
  isLoading?: boolean;
  isRealtimeLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => React.ReactNode;
  className?: string;
}

/**
 * Renders the React analytics dashboard page with summary cards and detail tables.
 *
 * @param props Page props containing dashboard aggregates, recent events, and loading state.
 * @returns A full-page React analytics dashboard composition.
 */
export function AnalyticsDashboardPage({
  data,
  recentEvents = [],
  funnelResult,
  cohortAnalysis,
  realtimeStats,
  conversionRate,
  isLoading,
  isRealtimeLoading,
  error,
  onRetry,
  renderSeoMeta,
  className,
}: AnalyticsDashboardPageProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const hasAnalysisSection =
    Boolean(funnelResult) ||
    Boolean(cohortAnalysis) ||
    Boolean(realtimeStats) ||
    Boolean(isRealtimeLoading) ||
    (conversionRate !== undefined && conversionRate !== null);
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.dashboardTitle"),
    description: copy.t("analytics.seo.dashboardDescription"),
    path: "/analytics",
    keywords: ["analytics", "traffic", "engagement", "funnels", "events"],
  });

  return (
    <>
      {renderSeoMeta ? renderSeoMeta(seoMeta) : null}
      <div
        className={cn(
          "flex min-h-screen flex-col gap-6 bg-background p-6 text-foreground",
          className,
        )}
        data-analytics-page="dashboard"
      >
        <FadeIn transition={getFadeInTransition()}>
          <header
            className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
            data-analytics-page-header
          >
            <div className="flex flex-col gap-1">
              <h1 className="text-3xl font-bold tracking-tight">
                {copy.t("analytics.dashboard.title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {copy.t("analytics.dashboard.subtitle")}
              </p>
            </div>
            {(realtimeStats || isRealtimeLoading) && (
              <RealtimeBadge
                stats={realtimeStats ?? null}
                isLoading={isRealtimeLoading ?? false}
              />
            )}
          </header>
        </FadeIn>

        {error && (
          <Card data-analytics-page-error>
            <CardContent className="pt-6">
              <div className="grid gap-3" role="alert">
                <span className="text-sm font-medium text-foreground">
                  {copy.t("analytics.dashboard.title")}
                </span>
                <span className="text-sm text-muted-foreground">
                  {getErrorMessage(error)}
                </span>
                {onRetry && (
                  <Button
                    className="w-fit"
                    type="button"
                    variant="secondary"
                    onClick={onRetry}
                  >
                    Retry
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <FadeIn
          transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.stats)}
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5"
          data-analytics-stats-row
        >
          <StatsCard
            label={copy.t("analytics.dashboard.pageViews")}
            value={data?.totalPageViews.toLocaleString() ?? "—"}
            icon={<DashboardIcon name="views" />}
          />
          <StatsCard
            label={copy.t("analytics.dashboard.uniqueVisitors")}
            value={data?.uniqueVisitors.toLocaleString() ?? "—"}
            icon={<DashboardIcon name="visitors" />}
          />
          <StatsCard
            label={copy.t("analytics.dashboard.topReferrers")}
            value={data?.topReferrers?.length?.toString() ?? "0"}
            icon={<DashboardIcon name="referrers" />}
          />
          <StatsCard
            label={copy.t("analytics.dashboard.devices")}
            value={Object.keys(data?.browserBreakdown ?? {}).length.toString()}
            icon={<DashboardIcon name="devices" />}
          />
          {conversionRate !== undefined && conversionRate !== null && (
            <StatsCard
              label={copy.t("analytics.dashboard.conversionRate")}
              value={`${conversionRate.toFixed(1)}%`}
              icon={<DashboardIcon name="conversion" />}
            />
          )}
        </FadeIn>

        {hasAnalysisSection && (
          <FadeIn
            transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.overview)}
            className="grid gap-4 xl:grid-cols-3"
            data-analytics-analysis-section
          >
            {(realtimeStats || isRealtimeLoading) && (
              <Card data-analytics-realtime-section>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-0">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">
                      {copy.t("analytics.dashboard.realtime")}
                    </CardTitle>
                    <CardDescription>
                      {copy.t("analytics.dashboard.realtimeDescription")}
                    </CardDescription>
                  </div>
                  <RealtimeBadge
                    stats={realtimeStats ?? null}
                    isLoading={isRealtimeLoading ?? false}
                  />
                </CardHeader>
                <CardContent className="grid gap-3 pt-4">
                  {(realtimeStats?.topActivePages ?? []).map((page) => (
                    <div
                      key={page.url}
                      className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
                      data-analytics-realtime-page
                    >
                      <span className="truncate text-muted-foreground">
                        {page.url}
                      </span>
                      <Badge variant="secondary">{page.visitors}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {funnelResult && (
              <FunnelChart result={funnelResult} className="xl:col-span-2" />
            )}
            {cohortAnalysis && (
              <Card className="xl:col-span-3" data-analytics-cohort-section>
                <CardHeader className="pb-0">
                  <CardTitle className="text-lg">
                    {copy.t("analytics.dashboard.cohorts")}
                  </CardTitle>
                  <CardDescription>
                    {copy.t("analytics.dashboard.cohortsDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 pt-4 md:grid-cols-2 xl:grid-cols-3">
                  {cohortAnalysis.cohorts.map((cohort) => (
                    <div
                      key={cohort.cohort}
                      className="grid gap-3 rounded-md border border-border bg-muted/30 p-4"
                      data-analytics-cohort-summary
                    >
                      <div className="flex items-center justify-between gap-3">
                        <strong className="text-sm text-foreground">
                          {cohort.cohort}
                        </strong>
                        <Badge variant="secondary">
                          {cohort.size.toLocaleString()}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {copy.t("analytics.dashboard.lastActivity", {
                          value: copy.formatTime(cohort.lastActivity),
                        })}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </FadeIn>
        )}

        <FadeIn
          transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.overview)}
        >
          <h2 className="sr-only">{copy.t("analytics.dashboard.overview")}</h2>
          <Card data-analytics-overview-section>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg" data-analytics-section-title>
                {copy.t("analytics.dashboard.overview")}
              </CardTitle>
            </CardHeader>
            <CardContent
              className="grid gap-4 pt-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]"
              data-analytics-grid
            >
              <TopPagesTable
                pages={data?.topPages ?? []}
                isLoading={isLoading}
              />
              <DeviceBreakdown
                breakdown={
                  data?.deviceBreakdown ?? { desktop: 0, tablet: 0, mobile: 0 }
                }
              />
            </CardContent>
          </Card>
        </FadeIn>

        <FadeIn
          transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.events)}
        >
          <h2 className="sr-only">
            {copy.t("analytics.dashboard.recentEvents")}
          </h2>
          <Card data-analytics-section>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg" data-analytics-section-title>
                {copy.t("analytics.dashboard.recentEvents")}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <EventTable
                events={recentEvents}
                isLoading={isLoading}
                maxRows={15}
              />
            </CardContent>
          </Card>
        </FadeIn>
      </div>
    </>
  );
}
